import * as React from "react";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import { useParams, useNavigate } from "react-router-dom";
import { Order } from "../../../types/api/Order";
import useAddShipping from "../../../queries/routes/useAddShipping";
import { useEffect, useState } from "react";
import {
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  CircularProgress,
} from "@mui/material";
import LineItem from "../../store/components/line-item/LineItem";
import GenieText from "../../../components/core/genie-text/GenieText";
import Box from "@mui/material/Box";
import { formatCurrency } from "../../../utils/currency";
import { useAuth0 } from "@auth0/auth0-react";
import { apiUrl } from "../../../utils/apiUrl";

interface Props {
  order: Order | null;
  open: boolean;
  setClosed: () => void;
}

export default function AddShippingDialog({ open, order, setClosed }: Props) {
  const { org_id } = useParams();
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();
  const [carrier, setCarrier] = useState<null | string>(
    order?.shipping_information ? order.shipping_information.carrier : "USPS"
  );
  const [trackingNumber, setTrackingNumber] = useState(
    order?.shipping_information
      ? order.shipping_information.tracking_number
      : ""
  );
  const [isLoadingPdf, setIsLoadingPdf] = useState(false);
  const { mutate: addShipping } = useAddShipping(org_id!, order?.id || "");

  useEffect(() => {
    setCarrier(
      order?.shipping_information ? order.shipping_information.carrier : "USPS"
    );
    setTrackingNumber(
      order?.shipping_information
        ? order.shipping_information.tracking_number
        : ""
    );
  }, [order?.shipping_information]);

  const handleClose = () => {
    setTrackingNumber("");
    setCarrier("USPS");
    setClosed();
  };

  const handlePrintPackingSlip = async () => {
    if (!order) return;

    setIsLoadingPdf(true);
    try {
      const accessToken = await getAccessTokenSilently();
      const response = await fetch(
        apiUrl(`/api/order/${order.id}/packing-slip`),
        {
          method: "GET",
          headers: {
            Accept: "application/pdf",
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (!response.ok) throw new Error("Failed to fetch PDF");
      const blob = await response.blob();
      const pdfUrl = URL.createObjectURL(blob);

      // Open PDF in new tab
      window.open(pdfUrl, "_blank");
    } catch (err) {
      console.error("Failed to fetch packing slip:", err);
    } finally {
      setIsLoadingPdf(false);
    }
  };

  if (!order) {
    return null;
  }

  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogTitle>Add Tracking Info</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Enter a tracking number for this order. This will send an email to the
          customer with the tracking link.
        </DialogContentText>

        <Divider sx={{ my: 2 }} />
        {order.cart.line_items.map((lineItem) => (
          <LineItem
            cart={order.cart}
            readonly={true}
            lineItem={lineItem}
            key={lineItem.id}
          />
        ))}

        <Divider sx={{ my: 2 }} />
        {order.shipping_address && (
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Box
              mb={2}
              display={"flex"}
              alignItems={"center"}
              justifyContent={"space-between"}
            >
              <GenieText fontSize={"small"}>
                Provider Selected: {order.shippo_provider}
              </GenieText>
              <GenieText fontSize={"small"}>
                {formatCurrency(order.shipping_cost_in_cents || 0)}
              </GenieText>
            </Box>

            <GenieText fontSize={"tiny"} fontWeight={"bold"}>
              Ship to:
            </GenieText>
            <GenieText fontSize={"tiny"}>
              {order.created_by_user.name}
            </GenieText>
            <GenieText fontSize={"tiny"}>
              {order.shipping_address.line1}
            </GenieText>
            <GenieText fontSize={"tiny"}>
              {order.shipping_address.line2}
            </GenieText>
            <GenieText fontSize={"tiny"}>
              {order.shipping_address.city}, {order.shipping_address.state}{" "}
              {order.shipping_address.postal_code}
            </GenieText>
          </Paper>
        )}

        {/* Print Packing Slip Link */}
        {order && (
          <Box sx={{ mt: 2, mb: 1 }}>
            <Button
              variant="outlined"
              color="primary"
              onClick={handlePrintPackingSlip}
              disabled={isLoadingPdf}
              sx={{ textTransform: "none", fontSize: 14 }}
            >
              {isLoadingPdf ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CircularProgress size={16} />
                  Creating...
                </Box>
              ) : (
                "Print Packing Slip"
              )}
            </Button>
          </Box>
        )}

        <FormControl fullWidth sx={{ mt: 2 }}>
          <InputLabel size={"small"} id="carrier-id-label">
            Carrier
          </InputLabel>
          <Select
            sx={{ width: "100%" }}
            labelId="carrier-id-label"
            id="account-type-select"
            label="Carrier"
            name="selected_type"
            value={carrier}
            onChange={(event) => setCarrier(event.target.value)}
          >
            <MenuItem value={"USPS"}>USPS</MenuItem>
            <MenuItem value={"FEDEX"}>FedEx</MenuItem>
            <MenuItem value={"UPS"}>UPS</MenuItem>
          </Select>
        </FormControl>
        <TextField
          sx={{ mt: 2 }}
          autoFocus
          margin="dense"
          id="name"
          label="Tracking Number"
          type="text"
          fullWidth
          variant="outlined"
          value={trackingNumber}
          onChange={(event) => setTrackingNumber(event.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          disabled={!carrier || carrier === "" || trackingNumber === ""}
          onClick={() => {
            if (order?.shipping_information) {
              addShipping({
                ...order.shipping_information,
                carrier: carrier || "",
                tracking_number: trackingNumber,
                status: "SHIPPED",
              });
            } else {
              addShipping({
                id: order?.shipping_information?.id || null,
                carrier: carrier || "",
                tracking_number: trackingNumber,
                status: "SHIPPED",
              });
            }
            handleClose();
          }}
        >
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
}
